/**
 * The training manuals, as data.
 *
 * One JSON file per manual is the single source of the words: the /guide pages
 * render it, and docs/manuals/build.py turns the same file into the branded
 * Word manual offered for download. Change the JSON, rebuild the .docx, and the
 * screen and the printout still agree.
 *
 * Text may carry two marks: **bold**, and [[a label]] for something the reader
 * sees on screen, such as a button.
 */

import cashier from "./cashier.json";
import manager from "./manager.json";
import setup from "./setup.json";

export type CalloutKind = "tip" | "note" | "warn" | "dont" | "why";

export type Block =
  | { type: "h2" | "h3" | "p"; text: string }
  | { type: "bullets" | "steps"; items: string[] }
  | { type: "callout"; kind: CalloutKind; title: string | null; body: string[] }
  | { type: "table"; head: string[]; rows: string[][]; widths?: number[] | null }
  | { type: "checklist"; title?: string | null; items: string[] }
  | { type: "practice"; title: string; tasks: string[] }
  | { type: "quiz"; items: { q: string; a: string }[] };

export interface Module {
  title: string;
  goal: string;
  minutes?: number | null;
  blocks: Block[];
}

export interface Manual {
  title: string;
  audience: string;
  subtitle: string;
  code: string;
  version: string;
  outcomes: string[];
  modules: Module[];
  reference: {
    title: string;
    intro?: string | null;
    head: string[];
    rows: string[][];
    note?: string | null;
  } | null;
  skills: string[];
}

export interface ManualEntry {
  slug: string;
  /** Who it is for, in a word or two, for the cards that link to it. */
  short: string;
  manual: Manual;
  /** The Word version, built from the same JSON, in public/manuals/. */
  docx: string;
}

export const MANUALS: ManualEntry[] = [
  {
    slug: "cashier",
    short: "Cashiers",
    manual: cashier as unknown as Manual,
    docx: "/manuals/smartspaza-cashier-manual.docx",
  },
  {
    slug: "manager",
    short: "Owners and managers",
    manual: manager as unknown as Manual,
    docx: "/manuals/smartspaza-manager-manual.docx",
  },
  {
    slug: "setup",
    short: "Setting up a till",
    manual: setup as unknown as Manual,
    docx: "/manuals/smartspaza-setup-manual.docx",
  },
];

export function findManual(slug: string | undefined): ManualEntry | undefined {
  return MANUALS.find((entry) => entry.slug === slug);
}

export function totalMinutes(manual: Manual): number {
  return manual.modules.reduce((sum, m) => sum + (m.minutes ?? 0), 0);
}
